import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import { z } from 'zod'
import { kSourceCaseLawSummaryV3 } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getResponseDebugContext, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { isAbortError } from '@/util/errors/is-abort-error'

export const CaseSummaryResponseSchema = z.object({ long_summary: z.string().nullable() })

/** Read the full stored summary only when a caselaw viewer needs its panel. */
export async function getCaseSummary(parentId: string, signal?: AbortSignal): Promise<string | null> {
  const requestUrl = `${kPaxtonAppApiRESTBaseUrl()}${kSourceCaseLawSummaryV3}?parent_id=${encodeURIComponent(parentId)}`
  const transactionId = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error('User not authenticated')

    const response = await fetch(requestUrl, {
      method: 'GET',
      signal,
      headers: { Authorization: `Bearer ${token}`, 'X-Transaction-Id': transactionId },
    })
    const { data, parseError } = await readResponseJson(response)

    if (!response.ok) throw new ApiError(response.status, 'Case summary request failed')
    if (parseError) throw new ApiResponsePayloadError(response.status, 'Invalid case summary response JSON')

    const parsed = CaseSummaryResponseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.captureException(new Error('Case summary response schema validation failed'), {
        extra: { ...getResponseDebugContext(response, requestUrl), issues: parsed.error.issues },
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid case summary response shape', parsed.error.issues)
    }
    return parsed.data.long_summary
  } catch (error) {
    if (isAbortError(error)) throw error
    if (!(error instanceof ApiError)) {
      Sentry.captureException(error, { extra: { requestUrl, transactionId } })
    }
    throw error
  }
}
